import { Card, CardContent, CardMedia, Typography, CardActions, Box, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, Favorite as FavoriteIcon, PlaylistAdd as PlaylistAddIcon, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLists } from '../contexts/ListContext';
import { Link as RouterLink } from 'react-router-dom';
import type { Product } from '../types/product';
import { useState } from 'react';
import AuthPromptDialog from './AuthPromptDialog';
import { formatGBP } from '../utils/currency';
import AddToListButton from './lists/AddToListButton';

interface ProductCardProps {
  product: Product;
  onEdit?: (product: Product) => void;
  className?: string;
}


const FavoriteAction = ({ product }: { product: Product }) => {
  const { isAuthenticated, isFavorite, toggleFavorite } = useAuth();
  const isFavorited = isAuthenticated && isFavorite(product.id);
  const [openPrompt, setOpenPrompt] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      toggleFavorite(product.id);
    } else {
      setOpenPrompt(true);
    }
  };

  return (
    <>
      <Tooltip title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
        <IconButton 
          size="small" 
          onClick={handleClick}
          color={isFavorited ? 'error' : 'default'}
          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorited ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <AuthPromptDialog 
        open={openPrompt} 
        onClose={() => setOpenPrompt(false)} 
        onSuccess={() => {
          setOpenPrompt(false);
          // After successful login, perform the favorite action
          toggleFavorite(product.id);
        }}
      />
    </>
  );
};

const AddToListAction = ({ product }: { product: Product }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingAnchor, setPendingAnchor] = useState<null | HTMLElement>(null);
  const [openPrompt, setOpenPrompt] = useState(false);
  const { lists } = useLists();
  const { isAuthenticated } = useAuth();
  const productLists = lists.filter(list => Array.isArray(list.productIds) && list.productIds.includes(product.id));

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      setAnchorEl(e.currentTarget);
    } else {
      setPendingAnchor(e.currentTarget);
      setOpenPrompt(true);
    }
  };

  return (
    <>
      <Tooltip title={`Add to list (${productLists.length})`}>
        <IconButton 
          size="small" 
          onClick={handleClick}
          aria-label="Add to list"
        >
          <PlaylistAddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      {anchorEl && (
        <AddToListButton 
          productId={product.id}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
        />
      )}
      <AuthPromptDialog 
        open={openPrompt} 
        onClose={() => setOpenPrompt(false)} 
        onSuccess={() => {
          setOpenPrompt(false);
          if (pendingAnchor) {
            setAnchorEl(pendingAnchor);
            setPendingAnchor(null);
          }
        }}
      />
    </>
  );
};

export const ProductCard = ({ product, onEdit, className }: ProductCardProps) => {
  const { isAdmin } = useAuth();
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Only prevent navigation for action buttons
    const target = e.target as HTMLElement;
    const isActionButton = target.closest('button[aria-label="edit product"]') || 
                         target.closest('button[title="Edit Product"]') ||
                         target.closest('button[aria-label="more actions"]');
    
    if (isActionButton) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  

  return (
    <Card 
      component="div"
      onClick={handleCardClick}
      className={className}
      sx={{
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: (theme) => theme.shadows[4],
        },
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 0,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 'none',
    }}>
      {/* Product Image */}
      <div     
      className="product-card-container"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box 
          className="product-card-image"
          component={RouterLink}
          to={`/products/${product.id}`}
          sx={{ 
            textDecoration: 'none', 
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            backgroundColor: 'background.default',
            height: { xs: '200px', sm: '280px', md: '360px' },
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <CardMedia
            component="img"
            loading="lazy"
            image={product.image || 'https://placehold.co/300x200/4f46e5/ffffff?text=No+Image'}
            alt={product.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              /* Remove zoom on hover */
              transition: 'none',
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://placehold.co/300x200/ef4444/ffffff?text=Image+Not+Found';
            }}
          />
          {/* Overlay actions on image */}
          <Box
            className="image-actions"
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              display: 'flex',
              gap: 0.5,
              bgcolor: 'rgba(255,255,255,0.92)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5
            }}
          >
            <FavoriteAction product={product} />
            <AddToListAction product={product} />
          </Box>
        </Box>

        {/* Product Content */}
        <CardContent className="product-card-content" sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>

        <Box className="content-container" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1, gap: 0.25 }}>
          {/* Title */}
          <Typography 
            variant="subtitle1" 
            component="h3" 
            sx={{ 
              fontWeight: 600,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            {product.name}
          </Typography>
          {product.brand && (
            <Typography
              variant="body2"
              color="primary.main"
              sx={{
                mt: 0.25,
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                flex: 1,
                opacity: 0.8,
              }}
            >
              {product.brand}
            </Typography>
          )}
          
          
        </Box>
        
        {/* Description 
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 1.5,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            flexGrow: 1,
          }}
        >
          
        </Typography>*/}
        
        {/* Rating section removed as per request */}
        <Box className="price-container" sx={{ mb: 1.5 }}>
          
          {/* Price */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {formatGBP(Number(product.price ?? 0))}
            </Typography>
          </Box>
        </Box>
        
        {/* Stock status, category, and tags removed as per request */}
        </CardContent>
        
        </div>

      {/* Admin Actions */}
      <CardActions sx={{ 
        p: 2, 
        pt: 0,
        justifyContent: 'space-between',
        mt: 'auto',
        width: '100%'
      }}>
        <Box>
          {isAdmin && onEdit && (
            <Tooltip title="Edit Product">
              <IconButton 
                size="small" 
                color="primary" 
                onClick={() => onEdit(product)}
                sx={{
                  flexShrink: 0,
                  backgroundColor: 'rgba(79, 70, 229, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(79, 70, 229, 0.15)',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
    );
  };
  

